<script>
    import router from "page";
    import { onMount } from "svelte";

    import Tabulator from "tabulator-tables";
    import "tabulator-tables/dist/css/tabulator.min.css";

    import { fetchGet, fetchPost } from "../utils/utilsFetch.js";

    import { removeFromCart, getCart } from "../utils/utils.js";

    import { addToCartFormatter } from "../utils/utilsTabulator.js";

    import {
        productPictureLinkFormatter,
        numberFormatter,
        oneDecimalFormatter,
        percentFormatter,
        keywordFilter,
        openProductModal,
    } from "../utils/utilsTabulator.js";

    import { isLoading, cart, messageObject, client, locale } from "../stores.js";

    let table, tableElement;

    onMount(async () => {
        $isLoading = true;

        table = new Tabulator(tableElement, {
            height: "100%",
            data: $cart,
            layout: "fitDataFill",
            cellVertAlign: "middle",
            columnCalcs: "both",
            columns: [
                {
                    title: "",
                    field: "sku",
                    formatter: productPictureLinkFormatter,
                },
                {
                    title: $locale.sku || "",
                    field: "sku",
                    headerFilter: "input",
                    headerFilterFunc: keywordFilter,
                    headerFilterPlaceholder: "⌕",
                    formatter: (cell, formatterParams, onRendered) => {
                        cell.getElement().style.fontWeight = "bold";
                        return cell.getValue();
                    },
                    cellClick: openProductModal,
                },
                {
                    title: $locale.product || "",
                    field: "productName",
                    width: $client.isHiddenPrices ? 600 : 300,
                    headerFilter: "input",
                    headerFilterFunc: keywordFilter,
                    headerFilterPlaceholder: "⌕",
                    formatter: (cell, formatterParams, onRendered) => {
                        cell.getElement().style.fontWeight = "bold";
                        return cell.getValue();
                    },
                    cellClick: openProductModal,
                },
                {
                    title: $locale.vat || "",
                    field: "vat",
                    formatter: percentFormatter,
                },
                {
                    title: $locale.price || "",
                    columns: [
                        {
                            title: $locale.withVat || "",
                            field: "priceWithVat",
                            formatter: oneDecimalFormatter,
                        },
                        {
                            title: $locale.withoutVat || "",
                            field: "priceWithoutVat",
                            formatter: oneDecimalFormatter,
                        },
                    ],
                },
                {
                    title: $locale.price || "",
                    columns: [
                        {
                            title: $locale.discount || "",
                            field: "discount",
                            formatter: percentFormatter,
                        },
                        {
                            title: $locale.withoutVat || "",
                            field: "clientPriceWithoutVat",
                            formatter: (cell, formatterParams, onRendered) => {
                                if (cell._cell.row.data.isCustomPrice) {
                                    cell.getElement().style.background =
                                        "rgba(255, 255, 0, 0.5)";
                                }

                                return cell.getValue();
                            },
                        },
                        {
                            title: $locale.withVat || "",
                            field: "clientPriceWithVat",
                            formatter: oneDecimalFormatter,
                        },
                    ],
                },
                {
                    title: $locale.total || "",
                    columns: [
                        {
                            title: $locale.withoutVat || "",
                            field: "totalWithoutVat",
                            formatter: numberFormatter,
                            topCalc: "sum",
                            topCalcFormatter: numberFormatter,
                        },
                        {
                            title: $locale.withVat || "",
                            field: "totalWithVat",
                            formatter: numberFormatter,
                            topCalc: "sum",
                            topCalcFormatter: numberFormatter,
                        },
                    ],
                },
                {
                    title: $locale.stock || "",
                    field: "stockHidden",
                    formatter: (cell) => {
                        let stock = cell.getValue().split(".")[0];

                        if (stock == ($locale.noStock || "Нема")) {
                            cell.getElement().style.color = "red";
                        }

                        return stock;
                    },
                },
                {
                    title: $locale.packaging || "",
                    field: "packaging",
                    headerFilter: "input",
                    headerFilterFunc: keywordFilter,
                    headerFilterPlaceholder: "⌕",
                },
                {
                    title: $locale.barcode || "",
                    field: "barcodes",
                    width: 200,
                    headerFilter: "input",
                    headerFilterFunc: keywordFilter,
                    headerFilterPlaceholder: "⌕",
                },
                {
                    title: $locale.order || "",
                    frozen: true,
                    formatter: addToCartFormatter,
                },
                {
                    frozen: true,
                    formatter: (cell) => {
                        let deleteButton = document.createElement("div");
                        deleteButton.className = "btn btn-red";
                        deleteButton.innerHTML = `<i class="fas fa-times no-margin">`;

                        deleteButton.addEventListener("click", async () => {
                            let data = cell._cell.row.data;
                            let sku = data.sku;

                            removeFromCart(sku);
                            table.replaceData(await getCart());
                        });
                        return deleteButton;
                    },
                },
            ],
        });

        if ($client.isHiddenPrices) {
            table.hideColumn("vat");
            table.hideColumn("priceWithVat");
            table.hideColumn("priceWithoutVat");
            table.hideColumn("discount");
            table.hideColumn("clientPriceWithoutVat");
            table.hideColumn("clientPriceWithVat");
            table.hideColumn("totalWithoutVat");
            table.hideColumn("totalWithVat");
        }

        $isLoading = false;
    });

    let inputAddress, inputPerson, inputPhone, inputNote;

    let showOrderSentSuccessfully;
    async function sendOrder() {
        $isLoading = true;

        let result = await fetchPost(`/api/cart`, {
            inputAddress,
            inputPerson,
            inputPhone,
            inputNote,
        });

        if (result.success) {
            showOrderSentSuccessfully = true;
            $cart = [];
        } else {
            $messageObject = {
                showMessage: true,
                type: "error",
                message: result.message,
            };
        }

        console.log("sendOrder result", result);
        $isLoading = false;
    }
</script>

{#if showOrderSentSuccessfully}
    <div class="success">
        <div class="message">
            <i class="fas fa-check" />
            {$locale.orderSentSuccessfully || ""}
        </div>
        <br />
        <div class="info">{$locale.checkEmailConfirmation || ""}</div>
    </div>
{:else}
    <div class="container">
        <div class="header">
            <b class="header-item">{$locale.newOrder || ""}</b>
            <input
                class="header-item"
                type="text"
                placeholder={$locale.address || ""}
                bind:value={inputAddress}
            />
            <input
                class="header-item"
                type="text"
                placeholder={$locale.person || ""}
                bind:value={inputPerson}
            />
            <input
                class="header-item"
                type="text"
                placeholder={$locale.phone || ""}
                bind:value={inputPhone}
            />
            <input
                class="header-item"
                type="text"
                placeholder={$locale.note || ""}
                bind:value={inputNote}
            />
            <button class="header-item btn btn-blue" on:click={sendOrder}>
                {$locale.placeOrder || ""}
            </button>
        </div>

        <div class="content">
            <div class="table" bind:this={tableElement} />
        </div>
    </div>
{/if}

<style>
    .container {
        display: grid;
        grid-template-columns: 1fr;
        /* calc sprecuva tabelata da ide nadvor od content*/
        /* grid-template-rows: 30px 45px calc(100vh - 95px);  */
        grid-template-rows: auto 1fr;
        height: 100%;
    }

    .header {
        grid-row: 1/2;

        display: flex;
        flex-wrap: wrap;
        align-items: center;
        /* background: #ccc; */
        padding: 10px;
    }

    .header-item {
        margin-right: 10px;
    }

    .content {
        padding: 0px 10px 10px 10px;
        grid-row: 2/-1;

        overflow: auto;
    }

    .table {
        border-radius: 3px;
    }

    .success {
        height: 100%;
        padding: 10px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }

    .message {
        font-size: 40px;

        display: flex;
    }

    .success i {
        margin-right: 30px;
        color: rgb(76, 182, 60);
    }

    .info {
        font-size: 20px;
        opacity: 0.5;
    }
</style>
